import { useRef, useEffect, useCallback } from "react";

// Color palette for error page game
export const COLORS = {
	// Light colors (good)
	light: {
		primary: "#fbbf24", // amber-400
		secondary: "#f59e0b", // amber-500
		glow: "rgba(251, 191, 36, 0.5)",
		particle: "#fde68a", // amber-200
	},
	// Blight colors (bad)
	blight: {
		primary: "#7c3aed", // violet-600
		secondary: "#5b21b6", // violet-800
		dark: "#1e1b4b", // indigo-950
		glow: "rgba(124, 58, 237, 0.5)",
	},
};

// Blight / enemy sprite filenames (quest/monsters/)
export const BLIGHT_MONSTER_FILES = ["Enemy1.gif", "Enemy3.gif", "Enemy4.gif"];

/**
 * Custom hook for game loop with consistent timing
 * @param {Function} update - Update function called each frame (no parameters - frame-based)
 * @param {Function} render - Render function called each frame
 * @param {boolean} isRunning - Whether the game loop should be running
 */
export const useGameLoop = (update, render, isRunning = true) => {
	const requestRef = useRef();

	const animate = useCallback(() => {
		update();
		render();
		requestRef.current = requestAnimationFrame(animate);
	}, [update, render]);

	useEffect(() => {
		if (isRunning) {
			requestRef.current = requestAnimationFrame(animate);
		}
		return () => {
			if (requestRef.current) {
				cancelAnimationFrame(requestRef.current);
			}
		};
	}, [isRunning, animate]);
};

/**
 * Custom hook for canvas setup with device pixel ratio support
 * @param {React.RefObject} canvasRef - Reference to canvas element
 * @param {number} width - Desired canvas width
 * @param {number} height - Desired canvas height
 */
export const useCanvas = (canvasRef, width, height) => {
	useEffect(() => {
		const canvas = canvasRef.current;
		if (!canvas) {
			return;
		}

		const ctx = canvas.getContext("2d");
		const dpr = window.devicePixelRatio || 1;

		// Set actual size in memory
		canvas.width = width * dpr;
		canvas.height = height * dpr;

		// Scale canvas back to desired size
		canvas.style.width = `${width}px`;
		canvas.style.height = `${height}px`;

		// Scale all drawing operations
		ctx.scale(dpr, dpr);
	}, [canvasRef, width, height]);
};
